/**
 * §8.6 Video Library — category filters + video card grid mock.
 */

import React, { useState } from 'react'
import clsx from 'clsx'
import {
  PlayCircle, Bot, LineChart, Wand2, Building2, CreditCard, GraduationCap,
  GitCommit, Sparkles, Search, Filter, Play,
} from 'lucide-react'
import { Container, Eyebrow, SectionHeading } from '@/components/landing/design-system'
import { VIDEO_CATS, VIDEO_SAMPLES } from '../data'
import { TONE } from '../_shared'

const ICONS = { PlayCircle, Bot, LineChart, Wand2, Building2, CreditCard, GraduationCap, GitCommit }

export default function Videos() {
  const [cat, setCat] = useState('all')
  const filtered = cat === 'all' ? VIDEO_SAMPLES : VIDEO_SAMPLES.filter((v) => v.cat === cat)

  return (
    <section id="videos" className="relative py-24 md:py-28 scroll-mt-24">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
      <Container size="wide" className="flex flex-col gap-10">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>Video Library</Eyebrow>
          <SectionHeading align="center"
            title={<>Watch, don&apos;t read.{' '}<span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">40+ videos indexed.</span></>}
            subtitle="Filter by module.  Every video is tagged, searchable and captioned — with running times shown up front."
          />
        </div>

        {/* search + filter */}
        <div className="flex flex-col md:flex-row items-stretch md:items-center gap-2">
          <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2 flex-1">
            <Search className="h-3.5 w-3.5 text-zinc-500" />
            <input readOnly defaultValue="strategy builder" className="flex-1 bg-transparent text-[12.5px] text-zinc-200 outline-none" />
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2 text-[11px] font-mono uppercase tracking-wider text-zinc-400">
            <Filter className="h-3 w-3" /> filter
          </span>
        </div>

        {/* category chips */}
        <div className="flex flex-wrap gap-1.5">
          <button onClick={() => setCat('all')}
            className={clsx('inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-mono uppercase tracking-wider',
              cat === 'all'
                ? 'border-brand-gold/40 bg-brand-gold/10 text-brand-gold'
                : 'border-white/10 bg-white/[0.03] text-zinc-400 hover:text-white',
            )}>
            All
          </button>
          {VIDEO_CATS.map((c) => {
            const on = cat === c.id
            const t = TONE[c.tone] || TONE.orange
            return (
              <button key={c.id} onClick={() => setCat(c.id)}
                className={clsx('inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-mono uppercase tracking-wider',
                  on ? 'text-white' : 'border-white/10 bg-white/[0.03] text-zinc-400 hover:text-white',
                )}
                style={on ? { borderColor: `${t.hex}66`, background: `${t.hex}20`, color: t.hex } : undefined}>
                {c.label}
              </button>
            )
          })}
        </div>

        {/* video grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          {filtered.map((v) => {
            const tone = TONE[v.tone] || TONE.orange
            const Icon = ICONS[v.iconKey] || PlayCircle
            return (
              <div key={v.id} className="relative rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-xl overflow-hidden transition-all hover:border-white/20 hover:bg-white/[0.03] group">
                {/* thumbnail */}
                <div className="relative aspect-video overflow-hidden">
                  <div className="absolute inset-0"
                    style={{ background: `radial-gradient(60% 50% at 30% 30%, rgba(${tone.rgb},0.30), transparent 70%), linear-gradient(135deg, rgba(${tone.rgb},0.10), rgba(0,0,0,0.6))` }} />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-black/40 backdrop-blur-md text-white group-hover:scale-105 transition-transform">
                      <Play className="h-4 w-4 fill-white" />
                    </span>
                  </div>
                  <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-md border border-white/15 bg-black/60 backdrop-blur px-1.5 py-0.5 text-[10px] font-mono text-white">
                    {v.duration}
                  </span>
                  <span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[9.5px] font-mono uppercase tracking-wider"
                    style={{ borderColor: `${tone.hex}55`, background: `${tone.hex}22`, color: tone.hex }}>
                    <Icon strokeWidth={1.8} style={{ width: 10, height: 10 }} />
                    {VIDEO_CATS.find((c) => c.id === v.cat)?.label}
                  </span>
                </div>
                <div className="p-3">
                  <div className="text-[13px] font-semibold text-white leading-tight">{v.title}</div>
                </div>
              </div>
            )
          })}
        </div>

        <div className="text-[12px] font-mono text-zinc-500 text-center">Showing {filtered.length} of 40+ videos</div>
      </Container>
    </section>
  )
}
